import { z } from 'zod';

/**
 * A person's name: letters (including accented/unicode ones), spaces,
 * hyphens, apostrophes and periods (for a middle initial like "Mary J.
 * Smith") - no digits or other symbols. Must start with a letter so a
 * string of only spaces/punctuation can't slip past `.trim()`.
 */
export const nameField = (label: string, maxLength = 50) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(maxLength, `${label} must be ${maxLength} characters or fewer`)
    .regex(
      /^[\p{L}][\p{L}'’.\- ]*$/u,
      `${label} can only contain letters, spaces, hyphens, apostrophes and periods`
    );

/**
 * A free-text label for something with a proper name that isn't a person's -
 * a job title, an institute, a course - so digits and the punctuation those
 * legitimately carry are allowed: "Level 2 Engineer", "AT&T", "UX/UI Designer
 * (Contract)", "Bachelor's (Hons)".
 */
const TITLE_LIKE_REGEX = /^[\p{L}\p{N}][\p{L}\p{N}&,.'’()/\- ]*$/u;
const TITLE_LIKE_MESSAGE = (label: string) =>
  `${label} can only contain letters, numbers, spaces and & , . ' ( ) / -`;

export const titleLikeField = (label: string, maxLength: number) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(maxLength, `${label} must be ${maxLength} characters or fewer`)
    .regex(TITLE_LIKE_REGEX, TITLE_LIKE_MESSAGE(label));

/** Same as `titleLikeField`, but the field itself is optional (e.g. a job title). */
export const optionalTitleLikeField = (label: string, maxLength: number) =>
  z
    .string()
    .trim()
    .max(maxLength, `${label} must be ${maxLength} characters or fewer`)
    .regex(TITLE_LIKE_REGEX, TITLE_LIKE_MESSAGE(label))
    .or(z.literal(''))
    .optional();
